import React from 'react';
import styled from '@benzinga/themetron';
import { DelayedQuote, AffiliateLinks } from '@benzinga/quotes-manager';
import { useTickerData } from '@benzinga/ticker-ui';
import { QuoteExchangeInformation, RankingTable, PortfolioPosition } from '@benzinga/quotes-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import type { DeviceType } from '@benzinga/device-utils';

import { StockQuotesTable } from '@benzinga/quotes-ui';

import { QuoteCard } from '@benzinga/quotes-ui';
import { Dividend, TradeAlertsData } from '@benzinga/calendar-manager';
import { TradeAlertVideos } from '@benzinga/plus-manager';

export interface QuotesBlockProps {
  attrs?: {
    align?: string;
    data: {
      bypass_paywall?: boolean;
      edge_paywall_label?: string;
      ticker_affiliate_links?: AffiliateLinks;
      tickers: string[];
      initialQuotes?: Record<string, DelayedQuote>;
      initialDividends?: Dividend[];
      tradeAlertsData?: TradeAlertsData[];
      portfolio_data?: PortfolioPosition[];
      layout?: string;
      tradeAlertVideos?: TradeAlertVideos;
      rankingTable?: RankingTable;
    };
  };
  tickers: any[];
  deviceType?: DeviceType;
}

export const QuotesBlock: React.FC<QuotesBlockProps> = ({ attrs, deviceType, tickers }) => {
  const symbols = tickers ?? attrs?.data?.tickers;
  const initialQuotes = attrs?.data?.initialQuotes ?? null;

  if (!symbols?.length) {
    return <div />;
  }

  // if (attrs?.layout === 'stock-list') {
  //   return <StockList symbols={symbols} />;
  // }

  const initialColumns = deviceType === 'mobile' ? 1 : 3;
  const initialDividends = attrs?.data?.initialDividends ?? [];

  return (
    <QuotesBlockWrapper className={attrs?.align}>
      {symbols.length > 1 || attrs?.data?.layout === 'table' ? (
        <StockQuotesTable
          affiliateLinks={attrs?.data?.ticker_affiliate_links}
          bypassPaywall={attrs?.data?.bypass_paywall}
          initialDividends={initialDividends}
          layout={attrs?.data?.layout}
          paywall_label={attrs?.data?.edge_paywall_label}
          portfolioData={attrs?.data?.portfolio_data ?? []}
          quotes={initialQuotes}
          rankingTable={attrs?.data?.rankingTable}
          symbols={symbols}
          tradeAlertsData={attrs?.data?.tradeAlertsData ?? []}
          tradeAlertVideos={attrs?.data?.tradeAlertVideos}
        />
      ) : (
        <div>
          {initialQuotes ? (
            <TickerBoxWrapper>
              <QuoteCard initialColumns={initialColumns} quote={initialQuotes[symbols[0]]} sessionType="REGULAR" />
            </TickerBoxWrapper>
          ) : (
            <TickerBox symbol={symbols[0]} />
          )}
        </div>
      )}
    </QuotesBlockWrapper>
  );
};

interface TickerBoxProps {
  initialQuote?: DelayedQuote;
  symbol: string;
  description?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const TickerBox: React.FC<TickerBoxProps> = ({ className, description, initialQuote, style, symbol }) => {
  const { stockQuote } = useTickerData(symbol);
  const quote = stockQuote ?? initialQuote;

  if (!quote) return null;

  return (
    <TickerBoxWrapper className={className} style={style}>
      {description && (
        <h2>
          {quote.name}{' '}
          <QuoteExchangeInformation exchange={quote.bzExchange} otcTier={quote.otcTier} symbol={quote.symbol} />
        </h2>
      )}
      {description && (
        <div className="quote-description" dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />
      )}
      <QuoteCard quote={quote} sessionType="REGULAR" />
      <div className="bz-pro-action">
        <a href="https://www.benzinga.com/go/benzinga-pro" rel="noreferrer" target="_blank">
          Start Your Free 14-Day Benzinga Pro Trial
        </a>
      </div>
    </TickerBoxWrapper>
  );
};

export const TickerBoxWrapper = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1000px;
  h2 {
    font-size: 30px;
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: #395173;
  }
  p {
    margin: 1rem 0;
  }
  .bz-pro-action {
    font-size: 15px;
    font-weight: 500;
  }
`;

export const QuotesBlockWrapper = styled.div`
  margin-bottom: 1rem;
  .quote-card-container {
    display: flex;
    flex-direction: column;
    border-width: 1px;
    background-color: white;
    box-shadow: ${({ theme }) => theme.shadow.md};
    border-color: rgb(156, 163, 175) !important;
    border-radius: 0.375rem;

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.lg};
    }
  }
`;
